import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FilterPanelProps {
  filters: {
    businessIds?: string[];
    automationType?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  };
  onFilterChange: (key: string, value: string | string[] | undefined) => void;
  onReset: () => void;
  businesses: Array<{ id: string; name: string }>;
  loading?: boolean;
}

export function FilterPanel({
  filters,
  onFilterChange,
  onReset,
  businesses,
  loading = false,
}: FilterPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  function handleMultiSelectChange(key: string, value: string) {
    const currentValues = (filters[key as keyof typeof filters] as string[]) || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v: string) => v !== value)
      : [...currentValues, value];

    onFilterChange(key, newValues.length > 0 ? newValues : undefined);
  }

  function isSelected(key: string, value: string) {
    const values = (filters[key as keyof typeof filters] as string[]) || [];
    return values.includes(value);
  }

  function getActiveFilterCount() {
    let count = 0;
    if (filters.businessIds && filters.businessIds.length > 0) count++;
    if (filters.automationType) count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.search) count++;
    return count;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">Loading filters...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        <div className="flex items-center gap-3">
          {getActiveFilterCount() > 0 && (
            <span className="text-xs font-semibold bg-blue-500 text-white px-2.5 py-1 rounded-full">
              {getActiveFilterCount()} active
            </span>
          )}
          <button
            onClick={onReset}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            title="Reset all filters">
            Reset
          </button>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            title={isCollapsed ? 'Expand filters' : 'Collapse filters'}>
            {isCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="p-6 space-y-6 max-h-[calc(100vh-250px)] overflow-y-auto">
          {/* Search */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Search</label>
            <input
              type="text"
              placeholder="Email subject or automation name..."
              value={filters.search || ''}
              onChange={e => onFilterChange('search', e.target.value || undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Businesses */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Businesses ({businesses.length})
            </label>
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
              {businesses.map(business => (
                <label
                  key={business.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={isSelected('businessIds', business.id)}
                    onChange={() => handleMultiSelectChange('businessIds', business.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-900 flex-1">{business.name}</span>
                </label>
              ))}
              {businesses.length === 0 && (
                <div className="py-8 text-center text-sm text-gray-500">No businesses found</div>
              )}
            </div>
          </div>

          {/* Automation Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Campaign Type</label>
            <div className="space-y-1">
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="automationType"
                  checked={filters.automationType === 'one_time'}
                  onChange={() => onFilterChange('automationType', 'one_time')}
                  className="border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm text-gray-900">Broadcasts</span>
              </label>
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="automationType"
                  checked={filters.automationType === 'trigger_based'}
                  onChange={() => onFilterChange('automationType', 'trigger_based')}
                  className="border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm text-gray-900">Trigger-based Automations</span>
              </label>
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="automationType"
                  checked={filters.automationType === 'date_based'}
                  onChange={() => onFilterChange('automationType', 'date_based')}
                  className="border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm text-gray-900">Date-based Automations</span>
              </label>
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name="automationType"
                  checked={!filters.automationType}
                  onChange={() => onFilterChange('automationType', undefined)}
                  className="border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm text-gray-900">All</span>
              </label>
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Date Range</label>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={e => onFilterChange('startDate', e.target.value || undefined)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-sm text-gray-500">to</span>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={e => onFilterChange('endDate', e.target.value || undefined)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
